import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Text } from "@geist-ui/core";
import { AlertDialog, Button as RadixButton, Flex } from "@radix-ui/themes";
import { useAuth0 } from "@auth0/auth0-react";
import * as Popover from "@radix-ui/react-popover";
import { Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Badge } from "@radix-ui/themes";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGenerateShadowModeTickets } from "@/api/ticket-event";
import { TicketEvent } from "@/api/ticket-event/ticket-event.model";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import {
  usePublishTopicFromDraft,
  usePublishGlobalInstructionsFromDraft,
} from "@/api/useRelease";

import { useSimulator } from "../../context/SimulatorWrapper";
import { TopicChange, useDraft } from "../../context/useDraft";

import styles from "./styles.module.scss";
import { ReleaseActions } from "./ReleaseActions";
import { useActivityLog } from "../../context/useActivityLog";
import { AdvancedFilters } from "./advanced-filters";
import {
  ApolloRolloutDialog,
  useApolloPercentageByTopic,
} from "./ApolloRollout";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";

interface ReleaseProps {
  onRelease: () => Promise<void>;
  isReleasing: boolean;
}

interface PublishTopicButtonProps {
  simulatorId: number;
  topicId: number;
  disabled?: boolean;
  onPublished?: () => void;
}

interface PublishGlobalInstructionsButtonProps {
  simulatorId: number;
  disabled?: boolean;
  onPublished?: () => void;
  draftContent: string;
}

const PublishGlobalInstructionsButton: React.FC<
  PublishGlobalInstructionsButtonProps
> = ({ simulatorId, disabled, onPublished, draftContent }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutateAsync: publishGlobalInstructions, isLoading } =
    usePublishGlobalInstructionsFromDraft(simulatorId);
  const clearsLiveInstructions = !draftContent.trim();

  const handleConfirm = useCallback(async () => {
    try {
      await publishGlobalInstructions({ simulatorId });
      toast.success(
        clearsLiveInstructions
          ? "Live global instructions removed"
          : "Global instructions published"
      );
      setConfirmOpen(false);
      onPublished?.();
    } catch (error) {
      console.error("Error publishing global instructions from draft:", error);
      toast.error("Failed to publish global instructions");
    }
  }, [
    publishGlobalInstructions,
    simulatorId,
    onPublished,
    clearsLiveInstructions,
  ]);

  return (
    <>
      <Tooltip.Provider>
        <Tooltip.Root delayDuration={0}>
          <Tooltip.Trigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={disabled || isLoading}
            >
              {isLoading ? <div className={styles.loadingSpinner} /> : "Publish"}
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content className={styles.tooltipContent} sideOffset={5}>
              {isLoading
                ? "Publishing..."
                : "Publish global instructions only (partial release)"}
              <Tooltip.Arrow className={styles.tooltipArrow} />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>

      <AlertDialog.Root
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!isLoading) {
            setConfirmOpen(open);
          }
        }}
      >
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>
            {clearsLiveInstructions
              ? "Remove live global instructions?"
              : "Replace live global instructions?"}
          </AlertDialog.Title>
          <AlertDialog.Description size="2">
            {clearsLiveInstructions
              ? "This will remove live global instructions. Topic drafts will not be released."
              : "Replace live global instructions with this draft. Topic drafts will not be released."}
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <RadixButton
                variant="soft"
                color="gray"
                disabled={isLoading}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </RadixButton>
            </AlertDialog.Cancel>
            <RadixButton
              variant="solid"
              color={clearsLiveInstructions ? "red" : "gray"}
              highContrast={!clearsLiveInstructions}
              disabled={isLoading}
              onClick={handleConfirm}
            >
              {isLoading
                ? "Publishing..."
                : clearsLiveInstructions
                  ? "Remove"
                  : "Publish"}
            </RadixButton>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  );
};

const PublishTopicButton: React.FC<PublishTopicButtonProps> = ({
  simulatorId,
  topicId,
  disabled,
  onPublished,
}) => {
  const { mutateAsync: publishTopic, isLoading } = usePublishTopicFromDraft(
    simulatorId,
    topicId,
  );

  const handleClick = useCallback(async () => {
    try {
      await publishTopic({ simulatorId, topicId });
      toast.success("Topic published successfully");
      onPublished?.();
    } catch (error) {
      console.error("Error publishing topic from draft:", error);
      toast.error("Failed to publish topic");
    }
  }, [publishTopic, simulatorId, topicId, onPublished]);

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={0}>
        <Tooltip.Trigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClick}
            disabled={disabled || isLoading}
          >
            {isLoading ? <div className={styles.loadingSpinner} /> : "Publish"}
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content className={styles.tooltipContent} sideOffset={5}>
            {isLoading
              ? "Publishing..."
              : "Publish this topic only (partial release)"}
            <Tooltip.Arrow className={styles.tooltipArrow} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export const Release: React.FC<ReleaseProps> = ({ onRelease, isReleasing }) => {
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingRelease, setLoadingRelease] = useState(false);

  const { user } = useAuth0();
  const {
    topics,
    simulator,
    publishedRelease,
    currentRelease,
    updateTopicReleasePolicy,
    isLoadingCurrentRelease,
    refreshReleaseData,
  } = useSimulator();
  const { topics: draftTopics, globalInstructions } = useDraft();

  const { activities } = useActivityLog({
    filters: {
      selectedReleaseId: currentRelease?.id || null,
      selectedFilters: [],
      sortOrder: "newest",
      searchQuery: "",
    },
  });

  if (!topics) return null;

  const totalTickets = 1000;
  const [totalTicketsCount, setTotalTicketsCount] = useState(0);
  const [isUpdatingTopicReleasePolicy, setIsUpdatingTopicReleasePolicy] =
    useState(false);
  const [updatingTopicIds, setUpdatingTopicIds] = useState<Set<number>>(
    new Set(),
  );

  const [locallyDisabledIds, setLocallyDisabledIds] = useState<Set<number>>(
    new Set(),
  );
  const [locallyEnabledIds, setLocallyEnabledIds] = useState<Set<number>>(
    new Set(),
  );

  const { mutateAsync: generateShadowModeTickets } =
    useGenerateShadowModeTickets();

  const selectedTopics = useMemo(() => {
    if (!topics) return [];

    return topics.filter((topic) => {
      if (locallyDisabledIds.has(topic.id)) return false;
      if (locallyEnabledIds.has(topic.id)) return true;

      const currentPolicy = currentRelease?.topicReleasePolicies?.find(
        (p) => p.topicId === topic.id,
      );
      if (currentPolicy) return currentPolicy.isEnabled;

      const publishedPolicy = publishedRelease?.topicReleasePolicies?.find(
        (p) => p.topicId === topic.id,
      );
      if (publishedPolicy) return publishedPolicy.isEnabled;

      // If currentRelease is not loaded yet, show topics that are enabled in published release
      // or topics with updates in draft
      if (!currentRelease && !isLoadingCurrentRelease) {
        // If there's no current release and not loading, only show published topics
        // publishedPolicy was already checked above, so if we reach here it's undefined
        return false;
      }

      const draft = draftTopics.find((d) => d.topicId === topic.id);
      // If draftTopics is empty (currentRelease not loaded), don't filter out topics
      // that have published policies - they should still show
      if (draftTopics.length === 0) {
        // If draftTopics is empty, check published policy again (it might exist but wasn't enabled)
        const publishedPolicyCheck =
          publishedRelease?.topicReleasePolicies?.find(
            (p) => p.topicId === topic.id,
          );
        return publishedPolicyCheck?.isEnabled ?? false;
      }

      return Boolean(draft?.hasUpdates);
    });
  }, [
    topics,
    currentRelease,
    publishedRelease,
    draftTopics,
    locallyDisabledIds,
    locallyEnabledIds,
    isLoadingCurrentRelease,
  ]);

  const enableUpdatedTopics = () => {
    if (!currentRelease?.id || !Array.isArray(selectedTopics)) {
      return;
    }

    const updatePromises = selectedTopics
      .map((topic) => {
        const currentPolicy = currentRelease.topicReleasePolicies?.find(
          (p) => p.topicId === topic.id,
        );
        if (!currentPolicy || !currentPolicy.isEnabled) {
          return updateTopicReleasePolicy(topic.id, currentRelease.id, {
            topicId: topic.id,
            releaseId: currentRelease.id,
            isEnabled: true,
          });
        }
        return null;
      })
      .filter(Boolean);

    setLoadingRelease(false);
    if (updatePromises.length > 0) {
      Promise.all(updatePromises).catch((error) => {
        console.error("Error updating topic release policies:", error);
      });
    }
  };

  const debouncedReload = useDebouncedCallback(enableUpdatedTopics, 1500);

  useEffect(() => {
    setLoadingRelease(true);
    debouncedReload();
  }, [selectedTopics, currentRelease]);

  const topicsForDropdown = useMemo(() => {
    return topics.filter(
      (topic) => !selectedTopics.some((st) => st.id === topic.id),
    );
  }, [topics, selectedTopics]);

  useEffect(() => {
    const abortController = new AbortController();

    const generateTickets = async () => {
      try {
        const matchingTickets = await generateShadowModeTickets({
          count: totalTickets,
          filterPolicy: currentRelease?.filterPolicy,
          releasePolicy:
            typeof currentRelease?.releasePolicy === "string"
              ? [
                  ...JSON.parse(currentRelease.releasePolicy),
                  ...selectedTopics.map((topic) => ({
                    type: "manual",
                    content: "Think carefully before answering",
                    topic: topic.topic,
                    subTopic: null,
                  })),
                ]
              : [],
          signal: abortController.signal,
        });

        setTotalTicketsCount(
          matchingTickets.filter((ticket: TicketEvent) => !ticket.shouldSkip)
            .length,
        );
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error("Error generating shadow mode tickets:", error);
        }
      }
    };

    if (simulator?.channel === "autonomous_ticket_event") {
      generateTickets();
    }

    return () => abortController.abort();
  }, [
    selectedTopics,
    generateShadowModeTickets,
    currentRelease,
    simulator?.channel,
  ]);

  const handleRemoveTopic = useCallback(
    async (topicId: number) => {
      if (!currentRelease?.id) return;

      setLocallyDisabledIds((prev) => new Set(prev).add(topicId));
      setLocallyEnabledIds((prev) => {
        const next = new Set(prev);
        next.delete(topicId);
        return next;
      });

      try {
        setUpdatingTopicIds((prev) => new Set(prev).add(topicId));

        await updateTopicReleasePolicy(topicId, currentRelease.id, {
          topicId,
          releaseId: currentRelease.id,
          isEnabled: false,
        });
      } catch (error) {
        console.error("Error removing topic:", error);
      } finally {
        setIsUpdatingTopicReleasePolicy(false);
        setUpdatingTopicIds((prev) => {
          const next = new Set(prev);
          next.delete(topicId);
          return next;
        });
      }
    },
    [currentRelease, updateTopicReleasePolicy],
  );

  const debouncedHandleRemoveTopic = useDebouncedCallback(
    useCallback(
      async (topicId: number) => {
        await handleRemoveTopic(topicId);
      },
      [handleRemoveTopic],
    ),
    1000,
  );

  const handleRemoveTopicWithDebounce = useCallback(
    (topicId: number) => {
      setIsUpdatingTopicReleasePolicy(true);
      debouncedHandleRemoveTopic(topicId);
    },
    [debouncedHandleRemoveTopic],
  );

  const handleAddTopic = useCallback(
    async (topicId: number) => {
      if (!currentRelease?.id) return;

      setLocallyEnabledIds((prev) => new Set(prev).add(topicId));
      setLocallyDisabledIds((prev) => {
        const next = new Set(prev);
        next.delete(topicId);
        return next;
      });

      try {
        setUpdatingTopicIds((prev) => new Set(prev).add(topicId));

        await updateTopicReleasePolicy(topicId, currentRelease.id, {
          topicId,
          releaseId: currentRelease.id,
          isEnabled: true,
        });
      } catch (error) {
        console.error("Error adding topic:", error);
      } finally {
        setIsUpdatingTopicReleasePolicy(false);
        setUpdatingTopicIds((prev) => {
          const next = new Set(prev);
          next.delete(topicId);
          return next;
        });
      }
    },
    [currentRelease, updateTopicReleasePolicy],
  );

  const debouncedHandleAddTopic = useDebouncedCallback(
    useCallback(
      async (topicId: number) => {
        await handleAddTopic(topicId);
      },
      [handleAddTopic],
    ),
    1000,
  );

  const handleAddTopicWithDebounce = useCallback(
    (topicId: number) => {
      setIsUpdatingTopicReleasePolicy(true);
      debouncedHandleAddTopic(topicId);
    },
    [debouncedHandleAddTopic],
  );

  const { workspace } = useWorkspace();
  const isApolloRolloutEnabled = true;

  const [isApolloRolloutOpen, setIsApolloRolloutOpen] = useState(false);
  const apolloPercentageByTopic = useApolloPercentageByTopic(
    simulator?.id,
    isApolloRolloutEnabled,
  );

  const topicsWithBadges = useMemo(() => {
    // Create a map of draft topics for quick lookup
    const draftTopicsMap = new Map(
      draftTopics.map((topic) => [topic.topicId, topic]),
    );

    // For each selected topic, create a badge entry
    // If topic is not in draftTopics, create a default entry
    return selectedTopics.map((topic) => {
      const draftTopic = draftTopicsMap.get(topic.id);

      if (draftTopic) {
        return {
          ...draftTopic,
          badges: {
            isLive: draftTopic.isLive,
            isNew: draftTopic.isNew,
            hasUpdates: draftTopic.hasUpdates,
            badges: [
              ...(draftTopic.isLive
                ? [{ type: "live", label: "Live", color: "green" as const }]
                : []),
              ...(draftTopic.isNew
                ? [{ type: "new", label: "New", color: "blue" as const }]
                : []),
              ...(draftTopic.hasUpdates
                ? [
                    {
                      type: "has-updates",
                      label: "Has Updates",
                      color: "blue" as const,
                    },
                  ]
                : []),
            ],
            sectionBadges: {
              brief: draftTopic.hasUpdates
                ? {
                    type: "has-updates",
                    label: "Has Updates",
                    color: "blue" as const,
                  }
                : null,
              instructions: draftTopic.hasUpdates
                ? {
                    type: "has-updates",
                    label: "Has Updates",
                    color: "blue" as const,
                  }
                : null,
            },
          },
          goodFeedbackCount: activities.filter(
            (a) =>
              a.type === "Session" &&
              a.sessionTags?.includes("Good Feedback") &&
              a.topic === draftTopic.topicName,
          ).length,
          badFeedbackCount: activities.filter(
            (a) =>
              a.type === "Session" &&
              a.sessionTags?.includes("Bad Feedback") &&
              a.topic === draftTopic.topicName,
          ).length,
        };
      }

      // Fallback for topics not in draftTopics
      const publishedPolicy = publishedRelease?.topicReleasePolicies?.find(
        (p) => p.topicId === topic.id,
      );
      const currentPolicy = currentRelease?.topicReleasePolicies?.find(
        (p) => p.topicId === topic.id,
      );

      return {
        topicId: topic.id,
        topicName: topic.topic,
        isEnabled:
          currentPolicy?.isEnabled ?? publishedPolicy?.isEnabled ?? false,
        isLive: publishedPolicy?.isEnabled ?? false,
        isNew: false,
        hasUpdates: false,
        instructions: currentPolicy?.instructions || [],
        prerequisites: currentPolicy?.prerequisites || [],
        conversations:
          currentRelease?.releaseConversations?.filter(
            (conv) => conv.topicId === topic.id,
          ) || [],
        badges: {
          isLive: publishedPolicy?.isEnabled ?? false,
          isNew: false,
          hasUpdates: false,
          badges: [
            ...(publishedPolicy?.isEnabled
              ? [{ type: "live", label: "Live", color: "green" as const }]
              : []),
          ],
          sectionBadges: {
            brief: null,
            instructions: null,
          },
        },
        goodFeedbackCount: activities.filter(
          (a) =>
            a.type === "Session" &&
            a.sessionTags?.includes("Good Feedback") &&
            a.topic === topic.topic,
        ).length,
        badFeedbackCount: activities.filter(
          (a) =>
            a.type === "Session" &&
            a.sessionTags?.includes("Bad Feedback") &&
            a.topic === topic.topic,
        ).length,
      };
    });
  }, [
    selectedTopics,
    draftTopics,
    activities,
    publishedRelease,
    currentRelease,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.simulatorIcon}>
          {simulator && (
            <SimulatorChannelIcon channel={simulator.channel} size="4" />
          )}
        </div>
        <div className={styles.headerContent}>
          <h1>Release {simulator?.name || "AI Agent"}</h1>
          <p>Choose where you want to publish these training updates to</p>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Flex justify="between" align="center">
            <div>
              <Text h4>Topics for Release</Text>
              <Text small>Review and manage topics for this release</Text>
            </div>
            {isApolloRolloutEnabled && simulator?.id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsApolloRolloutOpen(true)}
              >
                Apollo Rollout
              </Button>
            )}
          </Flex>
        </div>

        {draftTopics.length > 0 && (
          <Flex align="center" gap="2" className={styles.pendingTopics}>
            <Text small style={{ color: "var(--gray-11)" }}>
              {selectedTopics.length} of {topics?.length || 0} topics pending
              for release
            </Text>
          </Flex>
        )}

        <div className={styles.tableContainer}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Topic Name</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead>Conversations</TableHead>
                <TableHead>Feedback</TableHead>
                {isApolloRolloutEnabled && <TableHead>Apollo %</TableHead>}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topicsWithBadges.map((topicWithBadges) => {
                const topic = selectedTopics.find(
                  (t) => t.id === topicWithBadges.topicId,
                );
                if (!topic) return null;

                return (
                  <TableRow
                    key={topicWithBadges.topicId}
                    className={
                      updatingTopicIds.has(topicWithBadges.topicId)
                        ? styles.loadingRow
                        : undefined
                    }
                  >
                    <TableCell>
                      <Flex
                        align="center"
                        justify="between"
                        style={{ width: "100%" }}
                      >
                        <span>{topic.topic}</span>
                        <Flex gap="2" align="center">
                          {topicWithBadges.badges.badges.map(
                            (badge: any, index: number) => (
                              <Badge
                                key={index}
                                color={badge.color as any}
                                size="1"
                                style={
                                  badge.type === "live"
                                    ? {
                                        color: "var(--green-11)",
                                        backgroundColor: "var(--green-3)",
                                      }
                                    : undefined
                                }
                              >
                                {badge.label}
                              </Badge>
                            ),
                          )}
                        </Flex>
                      </Flex>
                    </TableCell>
                    <TableCell>
                      {topicWithBadges.instructions.length || 0}
                    </TableCell>
                    <TableCell>
                      {topicWithBadges.conversations.length || 0}
                    </TableCell>
                    <TableCell>
                      <div className="feedbackCounters">
                        <div className="counter positive">
                          {topicWithBadges.goodFeedbackCount}
                        </div>
                        <div className="counter negative">
                          {topicWithBadges.badFeedbackCount}
                        </div>
                      </div>
                    </TableCell>
                    {isApolloRolloutEnabled && (
                      <TableCell>
                        {apolloPercentageByTopic.get(topic.topic) ?? "0%"}
                      </TableCell>
                    )}
                    <TableCell>
                      <Flex gap="2" align="center">
                        {simulator?.id && (
                          <PublishTopicButton
                            simulatorId={simulator.id}
                            topicId={topicWithBadges.topicId}
                            disabled={
                              updatingTopicIds.has(topicWithBadges.topicId) ||
                              isReleasing
                            }
                          />
                        )}
                        <Tooltip.Provider>
                          <Tooltip.Root delayDuration={0}>
                            <Tooltip.Trigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleRemoveTopicWithDebounce(
                                    topicWithBadges.topicId,
                                  )
                                }
                                className={styles.removeButton}
                                disabled={
                                  updatingTopicIds.has(
                                    topicWithBadges.topicId,
                                  ) || isReleasing
                                }
                              >
                                {updatingTopicIds.has(
                                  topicWithBadges.topicId,
                                ) ? (
                                  <div className={styles.loadingSpinner} />
                                ) : (
                                  <Cross2Icon />
                                )}
                              </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                className={styles.tooltipContent}
                                sideOffset={5}
                              >
                                {updatingTopicIds.has(topicWithBadges.topicId)
                                  ? "Updating..."
                                  : isReleasing
                                    ? "Release in progress..."
                                    : "Remove from Release"}
                                <Tooltip.Arrow
                                  className={styles.tooltipArrow}
                                />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      </Flex>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className={styles.addTopicButton}>
          <Popover.Root>
            <Popover.Trigger asChild>
              <Button
                variant="outline"
                className={styles.actionButton}
                disabled={isUpdatingTopicReleasePolicy || isReleasing}
              >
                {isUpdatingTopicReleasePolicy ? (
                  <div className={styles.loadingSpinner} />
                ) : (
                  <PlusIcon />
                )}
                {isUpdatingTopicReleasePolicy
                  ? "Updating..."
                  : isReleasing
                    ? "Release in progress..."
                    : "Add Topic"}
              </Button>
            </Popover.Trigger>
            <Popover.Content
              className={styles.popoverContent}
              side="top"
              align="start"
              sideOffset={5}
            >
              <div className={styles.topicList}>
                {topicsForDropdown && topicsForDropdown.length > 0 ? (
                  topicsForDropdown.map((topic) => (
                    <Button
                      key={topic.id}
                      variant="ghost"
                      className={styles.topicButton}
                      onClick={() => {
                        handleAddTopicWithDebounce(topic.id);
                        document.body.click(); // Close popover after selection
                      }}
                      disabled={updatingTopicIds.has(topic.id) || isReleasing}
                    >
                      <div className={styles.topicContent}>
                        {updatingTopicIds.has(topic.id) ? (
                          <div className={styles.loadingSpinner} />
                        ) : (
                          <span>{topic.topic}</span>
                        )}
                        {updatingTopicIds.has(topic.id) && (
                          <span
                            style={{ fontSize: "12px", color: "var(--gray-8)" }}
                          >
                            Updating...
                          </span>
                        )}
                        {isReleasing && (
                          <span
                            style={{ fontSize: "12px", color: "var(--gray-8)" }}
                          >
                            Release in progress...
                          </span>
                        )}
                      </div>
                    </Button>
                  ))
                ) : (
                  <Text
                    small
                    style={{ color: "var(--gray-11)", padding: "8px 12px" }}
                  >
                    No topics available to add
                  </Text>
                )}
              </div>
              <Popover.Arrow />
            </Popover.Content>
          </Popover.Root>
        </div>
      </div>

      {globalInstructions.pending && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Flex justify="between" align="center">
              <div>
                <Text h4>Global Instructions</Text>
                <Text small>
                  These apply across topics and can be published without
                  releasing topic drafts
                </Text>
              </div>
              {simulator?.id && (
                <PublishGlobalInstructionsButton
                  simulatorId={simulator.id}
                  disabled={isReleasing || !publishedRelease}
                  onPublished={refreshReleaseData}
                  draftContent={globalInstructions.draftContent}
                />
              )}
            </Flex>
          </div>

          <Flex align="center" gap="2" className={styles.pendingTopics}>
            <Text small style={{ color: "var(--gray-11)" }}>
              Unpublished changes
            </Text>
          </Flex>
        </div>
      )}

      {currentRelease?.customToneOfVoice !==
        publishedRelease?.customToneOfVoice && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Flex justify="between" align="center">
              <div>
                <Text h4>Tone of Voice</Text>
                <Text small>
                  Tone of voice will change when publishing this release, can be
                  edited in configurations page
                </Text>
              </div>
            </Flex>
          </div>

          <Flex align="center" gap="2" className={styles.pendingTopics}>
            <Text small style={{ color: "var(--gray-11)" }}>
              This release: {currentRelease?.customToneOfVoice}
            </Text>
          </Flex>
        </div>
      )}

      {simulator?.channel === "autonomous_ticket_event" && (
        <>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Text h4>Select Audience</Text>
              <Text small>
                Choose the channel where Quack will engage users so the right
                support lands in the right place
              </Text>
            </div>
            <AdvancedFilters setLoadingFilters={setLoadingFilters} />
          </div>
        </>
      )}

      {isApolloRolloutEnabled && simulator?.id && (
        <ApolloRolloutDialog
          open={isApolloRolloutOpen}
          onOpenChange={setIsApolloRolloutOpen}
          simulatorId={simulator.id}
          topics={selectedTopics}
        />
      )}

      {currentRelease?.id && simulator?.id && (
        <ReleaseActions
          channel={simulator?.channel}
          onRelease={() => onRelease()}
          coverage={`${totalTicketsCount}/${totalTickets}`}
          disabled={isReleasing || loadingFilters || loadingRelease}
          isReleasing={isReleasing}
          releaseId={currentRelease?.id}
          simulatorId={simulator?.id}
        />
      )}
    </div>
  );
};
